import React, { useState } from 'react'
import {
  Globe,
  Plus,
  Eye,
  Edit,
  ExternalLink,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  Grid3X3,
  List
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { motion } from 'framer-motion'
import { apiService } from '../../services/api'
import toast from 'react-hot-toast'
import LoadingSpinner from '../common/LoadingSpinner'
import { Button } from '../ui/button'
import { Card, CardContent } from '../ui/card'
import { cn } from '../../utils/cn'
import { useHasPermission } from '../../hooks/usePermissions'

const getStatusColor = (status) => {
  switch (status) {
    case 'published': return 'text-success-600 bg-success-100 dark:bg-success-900/20 dark:text-success-400'
    case 'draft':     return 'text-warning-600 bg-warning-100 dark:bg-warning-900/20 dark:text-warning-400'
    default:          return 'text-neutral-600 bg-neutral-100 dark:bg-neutral-800 dark:text-neutral-400'
  }
}

const getStatusIcon = (status) => {
  switch (status) {
    case 'published': return CheckCircle
    case 'archived':  return AlertCircle
    default:          return Clock
  }
}

const getStatusStripColor = (status) => {
  switch (status) {
    case 'published': return 'bg-success-500'
    case 'draft':     return 'bg-warning-500'
    default:          return 'bg-neutral-300 dark:bg-neutral-600'
  }
}

const getLiveUrl = (site) => {
  if (site.custom_domain) {
    return `${site.custom_domain_ssl ? 'https' : 'http'}://${site.custom_domain}`
  }
  if (site.azure_published_url) return site.azure_published_url
  const clientSlug = site.company_name?.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'unknown'
  return `/sites/${clientSlug}/${site.slug}/`
}

const getRelativeTime = (timestamp) => {
  const diff = Date.now() - new Date(timestamp)
  const m = Math.floor(diff / 60000)
  const h = Math.floor(diff / 3600000)
  const d = Math.floor(diff / 86400000)
  if (m < 1)  return 'Just now'
  if (m < 60) return `${m}m ago`
  if (h < 24) return `${h}h ago`
  if (d < 7)  return `${d}d ago`
  return new Date(timestamp).toLocaleDateString('en-GB')
}

// Extract logo from site's editor meta_data
const getSiteEditorLogo = (site) => {
  try {
    const meta = typeof site.meta_data === 'string' ? JSON.parse(site.meta_data) : (site.meta_data || {})
    return meta.logo || null
  } catch {
    return null
  }
}

// Deterministic gradient per site name
const getBannerGradient = (name = '') => {
  const gradients = [
    'from-violet-500 via-purple-500 to-indigo-600',
    'from-accent-500 via-accent-600 to-purple-700',
    'from-rose-500 via-pink-500 to-fuchsia-600',
    'from-emerald-500 via-teal-500 to-cyan-600',
    'from-orange-500 via-amber-500 to-yellow-500',
    'from-blue-500 via-indigo-500 to-violet-600',
    'from-pink-500 via-rose-500 to-red-500',
    'from-teal-500 via-cyan-500 to-blue-500',
  ]
  const index = (name.charCodeAt(0) || 0) % gradients.length
  return gradients[index]
}

// ── Site Card (grid view) ──────────────────────────────────────────────────
const SiteCard = ({ site, canEditText, canDeleteSites, onDelete }) => {
  const StatusIcon = getStatusIcon(site.status)
  const initial = site.name?.charAt(0).toUpperCase() || '?'
  const isPublished = site.status === 'published'
  const gradient = getBannerGradient(site.name)
  const logoUrl = getSiteEditorLogo(site)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.02, transition: { duration: 0.18 } }}
    >
      <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-200">
        {/* Gradient top border */}
        <div className={cn('h-2 bg-gradient-to-r', gradient)} />

        <CardContent className="pt-4 pb-4">
          {/* Logo + name row */}
          <div className="flex items-center gap-3 mb-3">
            {logoUrl ? (
              <div className="w-12 h-12 rounded-2xl border border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
                <img
                  src={logoUrl}
                  alt={site.name}
                  className="w-full h-full object-contain p-1.5"
                  onError={(e) => {
                    e.currentTarget.parentElement.innerHTML = `<span class="text-base font-bold text-white w-full h-full flex items-center justify-center rounded-2xl bg-gradient-to-br ${gradient}">${initial}</span>`
                  }}
                />
              </div>
            ) : (
              <div className={cn('w-12 h-12 rounded-2xl bg-gradient-to-br flex items-center justify-center shrink-0 shadow-sm', gradient)}>
                <span className="text-base font-bold text-white select-none">{initial}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-neutral-900 dark:text-neutral-50 truncate leading-snug">{site.name}</h3>
              <div className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1', getStatusColor(site.status))}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {site.status.charAt(0).toUpperCase() + site.status.slice(1)}
              </div>
            </div>
            {canDeleteSites && (
              <button
                onClick={() => onDelete(site)}
                title="Delete site"
                className="p-1.5 rounded-lg text-neutral-400 hover:text-error-500 hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors shrink-0"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Quick-stats row */}
          <div className="flex items-center gap-3 text-xs text-neutral-500 dark:text-neutral-400 mb-4 pb-3 border-b border-neutral-100 dark:border-[#2f2b41]">
            <span className="flex items-center gap-1 shrink-0">
              <Clock className="w-3 h-3" />
              {getRelativeTime(site.updated_at)}
            </span>
            <span className="flex items-center gap-1 min-w-0">
              {site.custom_domain ? (
                <>
                  <Globe className="w-3 h-3 text-success-500 shrink-0" />
                  <span className="text-success-600 dark:text-success-400 truncate">{site.custom_domain}</span>
                </>
              ) : (
                <>
                  <Globe className="w-3 h-3 shrink-0" />
                  <span>No custom domain</span>
                </>
              )}
            </span>
          </div>

          {/* Actions — stacked for clear hierarchy */}
          <div className="flex flex-col gap-2">
            {canEditText && (
              <Button asChild variant="default" size="sm" className="w-full">
                <Link to={`/client/editor?site=${site.id}`}>
                  <Edit className="w-3.5 h-3.5 mr-1.5" />
                  Edit Site
                </Link>
              </Button>
            )}
            {isPublished ? (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => window.open(getLiveUrl(site), '_blank')}
              >
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                Visit Live Site
              </Button>
            ) : (
              <Button asChild variant="outline" size="sm" className="w-full">
                <Link to={`/client/preview/${site.id}`}>
                  <Eye className="w-3.5 h-3.5 mr-1.5" />
                  Preview
                </Link>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

// ── Site Row (list view) ───────────────────────────────────────────────────
const SiteRow = ({ site, canEditText, canDeleteSites, onDelete }) => {
  const StatusIcon = getStatusIcon(site.status)
  const isPublished = site.status === 'published'
  const gradient = getBannerGradient(site.name)
  const initial = site.name?.charAt(0).toUpperCase() || '?'
  const logoUrl = getSiteEditorLogo(site)

  return (
    <tr className="hover:bg-neutral-50 dark:hover:bg-[#201d2e]/70 transition-colors">
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          {/* Logo or gradient avatar */}
          {logoUrl ? (
            <div className="w-9 h-9 rounded-2xl border border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] flex items-center justify-center shrink-0 overflow-hidden shadow-sm">
              <img src={logoUrl} alt={site.name} className="w-full h-full object-contain p-1" />
            </div>
          ) : (
            <div className={cn('w-9 h-9 rounded-2xl bg-gradient-to-br flex items-center justify-center shrink-0 shadow-sm', gradient)}>
              <span className="text-sm font-bold text-white">{initial}</span>
            </div>
          )}
          <div>
            <div className="font-medium text-neutral-900 dark:text-neutral-50">{site.name}</div>
            <div className="text-sm text-neutral-500 dark:text-neutral-400">
              {site.custom_domain ? (
                <span className="flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  {site.custom_domain_ssl ? 'https' : 'http'}://{site.custom_domain}
                </span>
              ) : `/${site.slug}`}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className={cn('inline-flex items-center px-2 py-1 rounded-full text-xs font-medium', getStatusColor(site.status))}>
          <StatusIcon className="w-3 h-3 mr-1" />
          {site.status.charAt(0).toUpperCase() + site.status.slice(1)}
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-neutral-600 dark:text-neutral-300">
        {site.custom_domain ? (
          <span className="flex items-center gap-1">
            <Globe className="w-3 h-3 text-success-500" />
            <span className="text-success-600 dark:text-success-400">{site.custom_domain}</span>
            {site.custom_domain_ssl && <CheckCircle className="w-3 h-3 text-success-500" />}
          </span>
        ) : (
          <span className="text-neutral-400 dark:text-neutral-500">No custom domain</span>
        )}
      </td>
      <td className="px-6 py-4 text-sm text-neutral-600 dark:text-neutral-300">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3 text-neutral-400" />
          {getRelativeTime(site.updated_at)}
        </span>
      </td>
      <td className="px-6 py-4">
        <div className="flex gap-2">
          {canEditText && (
            <Button asChild variant="ghost" size="sm">
              <Link to={`/client/editor?site=${site.id}`} title="Edit">
                <Edit className="w-4 h-4" />
              </Link>
            </Button>
          )}
          {isPublished ? (
            <Button variant="ghost" size="sm" title="Visit live site" onClick={() => window.open(getLiveUrl(site), '_blank')}>
              <ExternalLink className="w-4 h-4" />
            </Button>
          ) : (
            <Button asChild variant="ghost" size="sm">
              <Link to={`/client/preview/${site.id}`} title="Preview">
                <Eye className="w-4 h-4" />
              </Link>
            </Button>
          )}
          {canDeleteSites && (
            <Button
              variant="ghost"
              size="sm"
              title="Delete site"
              onClick={() => onDelete(site)}
              className="text-error-500 hover:text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  )
}

// ── Main component ─────────────────────────────────────────────────────────
const ClientSites = () => {
  const [view, setView] = useState('grid')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [siteToDelete, setSiteToDelete] = useState(null)
  const canEditText = useHasPermission('can_edit_text')
  const canDeleteSites = useHasPermission('can_delete_sites')
  const queryClient = useQueryClient()

  const { data: sites, isLoading } = useQuery(
    'client-sites',
    () => apiService.get('/sites').then(r => r.data.sites || []),
    { staleTime: 30000 }
  )

  const deleteSiteMutation = useMutation(
    (siteId) => apiService.delete(`/sites/${siteId}`),
    {
      onSuccess: () => {
        toast.success('Site deleted successfully!')
        queryClient.invalidateQueries(['client-sites'])
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete site')
      }
    }
  )

  const handleDeleteSite = (site) => {
    setSiteToDelete(site)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = () => {
    if (siteToDelete) {
      deleteSiteMutation.mutate(siteToDelete.id)
      setShowDeleteConfirm(false)
      setSiteToDelete(null)
    }
  }

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDeleteConfirm && !event.target.closest('.modal-content')) {
        setShowDeleteConfirm(false)
        setSiteToDelete(null)
      }
    }
    if (showDeleteConfirm) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDeleteConfirm])

  const displaySites = Array.isArray(sites) ? sites : []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading sites..." />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">My Sites</h1>
          <p className="text-neutral-600 dark:text-neutral-400">Manage and create your websites</p>
        </div>

        <div className="flex items-center space-x-3">
          {/* View toggle */}
          <div className="flex rounded-lg border border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#1a1625]">
            <Button
              onClick={() => setView('grid')}
              variant="ghost"
              size="sm"
              className={cn(
                'rounded-r-none border-0',
                view === 'grid'
                  ? 'bg-accent-600 text-white hover:bg-accent-700 dark:bg-accent-600 dark:text-white dark:hover:bg-accent-700'
                  : 'text-neutral-600 hover:text-accent-600 hover:bg-neutral-50 dark:text-neutral-300 dark:hover:text-accent-300 dark:hover:bg-[#201d2e]'
              )}
            >
              <Grid3X3 className="h-4 w-4 mr-1" />
              Grid
            </Button>
            <Button
              onClick={() => setView('list')}
              variant="ghost"
              size="sm"
              className={cn(
                'rounded-l-none border-0',
                view === 'list'
                  ? 'bg-accent-600 text-white hover:bg-accent-700 dark:bg-accent-600 dark:text-white dark:hover:bg-accent-700'
                  : 'text-neutral-600 hover:text-accent-600 hover:bg-neutral-50 dark:text-neutral-300 dark:hover:text-accent-300 dark:hover:bg-[#201d2e]'
              )}
            >
              <List className="h-4 w-4 mr-1" />
              List
            </Button>
          </div>

          <Button asChild>
            <Link to="/client/sites/new">
              <Plus className="h-4 w-4 mr-2" />
              Create Site
            </Link>
          </Button>
        </div>
      </div>

      {/* Sites content */}
      {displaySites.length > 0 ? (
        view === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displaySites.map((site) => (
              <SiteCard
                key={site.id}
                site={site}
                canEditText={canEditText}
                canDeleteSites={canDeleteSites}
                onDelete={handleDeleteSite}
              />
            ))}
          </div>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200 dark:divide-[#2f2b41]">
                <thead className="bg-neutral-50 dark:bg-[#201d2e]">
                  <tr>
                    {['Site', 'Status', 'Domain', 'Updated', 'Actions'].map(h => (
                      <th key={h} className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-300 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-[#1a1625] divide-y divide-neutral-200 dark:divide-[#2f2b41]">
                  {displaySites.map((site) => (
                    <SiteRow
                      key={site.id}
                      site={site}
                      canEditText={canEditText}
                      canDeleteSites={canDeleteSites}
                      onDelete={handleDeleteSite}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )
      ) : (
        <Card>
          <CardContent className="text-center py-12">
            <Globe className="h-16 w-16 text-neutral-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 mb-2">No sites yet</h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-6">Get started by creating your first website</p>
            <Button asChild>
              <Link to="/client/sites/new">
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Site
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && siteToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="modal-content bg-white dark:bg-[#221f2f] rounded-lg p-6 max-w-md w-full mx-4 border border-neutral-200 dark:border-[#2f2b41]">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-error-100 dark:bg-error-900/20 rounded-full">
              <Trash2 className="w-6 h-6 text-error-600 dark:text-error-400" />
            </div>
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Delete Site</h3>
              <p className="text-neutral-600 dark:text-neutral-400">
                Are you sure you want to delete <span className="font-medium text-neutral-900 dark:text-neutral-100">"{siteToDelete.name}"</span>? This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => { setShowDeleteConfirm(false); setSiteToDelete(null) }}
                disabled={deleteSiteMutation.isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteConfirm}
                disabled={deleteSiteMutation.isLoading}
              >
                {deleteSiteMutation.isLoading ? (
                  <><LoadingSpinner size="sm" className="mr-2" />Deleting...</>
                ) : (
                  <><Trash2 className="w-4 h-4 mr-2" />Delete Site</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClientSites
